import { useMemo, useState } from 'react';
import { t } from '@lingui/macro';
import { useNavigate } from 'react-router-dom';
import uniqBy from 'lodash/uniqBy';
import isEmpty from 'lodash/isEmpty';

import BarChart from '@decisiv/iconix/lib/components/BarChart';
import Grid from '@decisiv/iconix/lib/components/Grid';
import ChessCastle from '@decisiv/iconix/lib/components/ChessCastle';
import Network from '@decisiv/iconix/lib/components/Network';
import Truck from '@decisiv/iconix/lib/components/Truck';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import Loading from '@decisiv/ui-components/lib/components/Loading';
import Menu, { MenuItem } from '@decisiv/ui-components/lib/components/Menu';
import Popover from '@decisiv/ui-components/lib/components/Popover';

import { useCurrentUserContext } from 'utils/User';
import ROUTE_NAMES from 'setup/Routing/routeNames';
import useSentinelTeam from 'hooks/useSentinelTeam';
import useFeatureFlag, { FEATURE_FLAGS } from 'utils/useFeatureFlag';

import useUserAppsLinksQuery from './useUserAppsLinksQuery';

import { Button } from './styles';

export default function AppsLinks() {
  const [active, setActive] = useState(false);
  const navigate = useNavigate();

  const { isSuperUser, isOemManager } = useCurrentUserContext();
  const { belongsToOemSegment } = useSentinelTeam();
  const { data, loading } = useUserAppsLinksQuery();

  const pd1188FFEnabled = useFeatureFlag(
    FEATURE_FLAGS.PD_1188_ROUTE_BASED_FLEET_MANAGEMENT,
  );

  const portalLinks = useMemo(
    () => data?.currentUserProfile?.user?.portalUrls || [],
    [data?.currentUserProfile?.user?.portalUrls],
  );

  const portalLinksItems = useMemo(
    () =>
      uniqBy(
        portalLinks.map(
          (portalLink: { id: string; productName: string; url: string }) => {
            const productName = portalLink.productName.toUpperCase();

            return {
              id: portalLink.id,
              text: t`${productName} PORTAL`,
              onSelect: () => {
                window.open(portalLink.url, '_blank', 'noopener,noreferrer');
              },
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              decoration: {
                type: 'Badge',
                icon: ChessCastle,
                color: 'success',
                palette: 'pastel',
              },
            } satisfies MenuItem;
          },
        ),
        'text',
      ),
    [portalLinks],
  );

  const insiteItem = useMemo(
    () =>
      ({
        id: 'insite',
        text: t`Insite`,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        labelDetails: '',
        onSelect: () => {
          window.open(
            data?.currentUserProfile?.user?.insiteUrl,
            '_blank',
            'noopener,noreferrer',
          );
        },
        decoration: {
          type: 'Badge',
          icon: BarChart,
          color: 'carrotCake',
          palette: 'pastel',
        },
      } satisfies MenuItem),
    [data?.currentUserProfile?.user?.insiteUrl],
  );

  const managementItem = useMemo(
    () =>
      ({
        id: 'management',
        text: t`Fleet Management`,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onSelect: () => {
          navigate(ROUTE_NAMES.admin.management);
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        decoration: {
          type: 'Badge',
          icon: Network,
          color: 'information',
          palette: 'pastel',
        },
      } satisfies MenuItem),
    [navigate],
  );

  const fleetItem = useMemo(
    () =>
      ({
        id: 'fleetItem',
        text: t`PSSM Fleet`,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onSelect: () => {
          navigate(ROUTE_NAMES.service_events);
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        decoration: {
          type: 'Badge',
          icon: Truck,
          color: 'warning',
          palette: 'pastel',
        },
      } satisfies MenuItem),
    [navigate],
  );

  const items = useMemo(() => {
    const baseItems =
      pd1188FFEnabled && (isSuperUser || isOemManager)
        ? [managementItem, fleetItem]
        : [];

    let visibleItems: MenuItem[] = [...baseItems];

    if (data?.currentUserProfile?.user?.insiteUrl)
      visibleItems = [...visibleItems, insiteItem];

    if (isSuperUser || !belongsToOemSegment)
      visibleItems = [...visibleItems, ...portalLinksItems];

    return visibleItems;
  }, [
    pd1188FFEnabled,
    isSuperUser,
    isOemManager,
    managementItem,
    fleetItem,
    portalLinksItems,
    data?.currentUserProfile?.user?.insiteUrl,
    insiteItem,
    belongsToOemSegment,
  ]);

  const handleToggleActive = () => {
    setActive((oldState) => !oldState);
  };

  if (!loading && isEmpty(items)) return null;

  return (
    <Popover
      zIndex={10}
      onShow={handleToggleActive}
      onHide={handleToggleActive}
      target={
        <Button aria-label={t`Show Apps links`} active={active}>
          <Grid />
        </Button>
      }
      showArrow={false}
    >
      {loading ? (
        <Flex padding={2}>
          <Loading title={t`Loading...`} size="small" />
        </Flex>
      ) : (
        <Menu items={items} />
      )}
    </Popover>
  );
}
